import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  Box, Tabs, Tab, Typography, IconButton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Divider, Snackbar
} from '@mui/material';
import {
  ContentCopyRounded
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import LoadableOverlay from '../Common/LoadingOverlay';
import AppContext from '../../AppContext';
import CollapsibleCodeBlock from 'components/Common/CollapsibleCodeBlock';
import MonacoEditorWithFullscreen from 'components/Common/MonacoEditorWithFullscreen';

const ResponseBodyComponent = ({ entity }) => {
  const selectedSidebarState = useSelector((state) => state.user.selectedSidebarState);
  const [currentResponse, setCurrentResponse] = useState(entity?.request?.response?.body) || {};
  const [selectedTab, setSelectedTab] = useState('pretty');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar message

  const { sendClicked, setSendClicked } = useContext(AppContext);

  // Memoize expensive computations
  const responseData = useMemo(() => ({
    body: entity?.request?.response?.body,
    status: entity?.request?.response?.status || 200,
    time: entity?.request?.response?.time || 0,
    headers: entity?.request?.response?.headers
  }), [entity?.request?.response]);

  useEffect(() => {
    setCurrentResponse(responseData.body || {});
  }, [responseData.body]);

  useEffect(() => {
    if (sendClicked) {
      setSendClicked(false);
    }
  }, [sendClicked, selectedSidebarState]);

  const tabsData = useMemo(() => [
    { label: "Pretty", component: "pretty" },
    { label: "Raw", component: "raw" },
    { label: "Response Headers", component: "response_headers" }
  ], []);

  const copyToClipboard = useCallback(async () => {
    try {
      let content;

      if (selectedTab === 'pretty') {
        content = JSON.stringify(currentResponse, null, 2);
      } else if (selectedTab === 'raw') {
        content = typeof currentResponse === 'object' ? JSON.stringify(currentResponse) : currentResponse;
      } else if (selectedTab === 'preview') {
        content = typeof currentResponse === 'object' ? JSON.stringify(currentResponse) : currentResponse;
      } else {
        content = ''; // No content for headers tab
      }

      await navigator.clipboard.writeText(content);
      setSnackbarMessage('Copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy: ', err);
      setSnackbarMessage('Failed to copy to clipboard');
      setSnackbarOpen(true);
    }
  }, [selectedTab, currentResponse]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const getStatusColor = useCallback((status) => {
    if (status >= 200 && status < 300) {
      return { backgroundColor: '#d4edda', color: '#155724' }; // Green background for success
    } else if (status >= 400 && status < 600) {
      return { backgroundColor: '#f8d7da', color: '#721c24' }; // Red background for error
    }
    return {};
  }, []);

  const isJsonString = useCallback((str) => {
    if (typeof str !== 'string') return false;
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
      return false;
    }
  }, []);

  const ResponseHeadersTable = useCallback(() => {
    const headers = responseData.headers;

    return (
      <TableContainer sx={{ borderRadius: '5px', marginBottom: 5, overflowX: 'hidden', overflowY: 'auto' }}>
        <Table size="small" aria-label="simple" sx={{ border: '1px solid var(--borderColor)', width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: '550', fontSize: '11px', whiteSpace: 'normal', wordWrap: 'break-word' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: '550', fontSize: '11px', whiteSpace: 'normal', wordWrap: 'break-word' }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {headers && Object.entries(headers).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ fontWeight: '500', fontSize: '11px', whiteSpace: 'normal', wordWrap: 'break-word' }}>{key}</TableCell>
                <TableCell sx={{ fontWeight: '500', fontSize: '11px', whiteSpace: 'normal', wordWrap: 'break-word' }}>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [responseData.headers]);

  // Memoize the JSON string for Monaco Editor to prevent re-parsing on every render
  const formattedJsonString = useMemo(() => {
    if (typeof currentResponse === 'string' && isJsonString(currentResponse)) {
      try {
        // Validate and format the JSON
        const parsed = JSON.parse(currentResponse);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return currentResponse;
      }
    }
    return currentResponse;
  }, [currentResponse, isJsonString]);

  const statusColorStyle = useMemo(() =>
    getStatusColor(responseData.status),
    [getStatusColor, responseData.status]
  );

  return (
    <Box style={{ width: '100%', padding: '4px 11px', maxWidth: '100%' }}>
      <Box sx={{ position: 'sticky', top: 0, backgroundColor: 'var(--background-primary)', zIndex: 99 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Tabs
              value={selectedTab}
              onChange={(event, newValue) => setSelectedTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                height: '32px', minHeight: '32px'
              }}
            >
              {tabsData.map((tab) => (
                <Tab
                  key={tab.component}
                  label={tab.label}
                  value={tab.component}
                  sx={{
                    textTransform: 'capitalize',
                    height: '30px',
                    minHeight: '30px',
                    color: 'var(--text-secondary) !important',
                    backgroundColor: 'transparent',
                    fontSize: '11px',
                  }}
                />
              ))}
            </Tabs>

            <IconButton onClick={copyToClipboard} size="small" sx={{ marginLeft: 2 }}>
              <ContentCopyRounded fontSize="small" />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center">
            <Typography variant="body2" sx={{ ...statusColorStyle, fontWeight: '500', fontSize: '11px', paddingRight: '8px', paddingLeft: '8px', paddingTop: '2px', paddingBottom: '2px', borderRadius: '4px' }}>
              Status Code: {responseData.status}
            </Typography>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 'auto', height: '20px', borderColor: 'gray' }} />

            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: '500', fontSize: '11px' }}>
              Time: {responseData.time} ms
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box mt={2} style={{ width: '100%', height:"auto", border: "1px solid var(--borderColor)", borderRadius: '4px' }}>
        {selectedTab === 'pretty' && (
          typeof currentResponse === 'string' && isJsonString(currentResponse) ? (
            <MonacoEditorWithFullscreen
              height="50vh"
              options={{
                readOnly: true,
                formatOnPaste: true,
                minimap: { enabled: false }, // Disable minimap for better performance
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true
              }}
              language="json"
              value={formattedJsonString}
            />
          ) : (
            <CollapsibleCodeBlock content={typeof currentResponse === 'string' ? currentResponse : JSON.stringify(currentResponse, null, 2)} language="html" />
          )
        )}
        {selectedTab === 'raw' && (
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-all', fontWeight: "400", fontSize: "12px", color: "var(--text-primary)" }}>
            {typeof currentResponse === 'object' ? JSON.stringify(currentResponse) : currentResponse}
          </pre>)}
        {selectedTab === 'response_headers' && (
          <Box style={{ overflowX: 'hidden' }}>
            <ResponseHeadersTable />
          </Box>
        )}
      </Box>

      <LoadableOverlay isLoading={isLoading} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box >
  );
};

export default ResponseBodyComponent;
